"use client";

import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import * as React from "react";
import { cn } from "@/util/string";

/**
 * Premium Tabs component built on top of Base UI.
 * Features: Glassmorphism, gradient accents, smooth animations, and modern aesthetics.
 *
 * Supports both uncontrolled (defaultValue) and controlled (value + onValueChange) modes.
 */

interface TabsProps
	extends Omit<
		React.ComponentPropsWithoutRef<typeof BaseTabs.Root>,
		"value" | "onValueChange"
	> {
	/** Current tab value (controlled mode) */
	value?: string | number | null;
	/** Callback when tab changes (controlled mode) */
	onValueChange?: (value: string | number | null) => void;
	/** Default tab value (uncontrolled mode) */
	defaultValue?: string | number | null;
}

export const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
	({ className, value, onValueChange, defaultValue, ...props }, ref) => (
		<BaseTabs.Root
			ref={ref}
			className={cn("flex w-full flex-col", className)}
			value={value}
			onValueChange={onValueChange}
			defaultValue={defaultValue}
			{...props}
		/>
	),
);
Tabs.displayName = "Tabs";

export const TabsList = React.forwardRef<
	HTMLDivElement,
	React.ComponentPropsWithoutRef<typeof BaseTabs.List>
>(({ className, ...props }, ref) => (
	<BaseTabs.List
		ref={ref}
		className={cn(
			// Container styling with subtle glassmorphism
			"relative z-0 flex w-fit gap-1 p-1 rounded-xl",
			// Background with glass effect
			"bg-base-200/50 backdrop-blur-sm",
			// Subtle border
			"ring-1 ring-base-content/5",
			className,
		)}
		{...props}
	/>
));
TabsList.displayName = "TabsList";

export const TabsTab = React.forwardRef<
	HTMLButtonElement,
	React.ComponentPropsWithoutRef<typeof BaseTabs.Tab>
>(({ className, ...props }, ref) => (
	<BaseTabs.Tab
		ref={ref}
		className={cn(
			// Base layout
			"relative flex h-9 items-center justify-center px-4 text-sm font-medium whitespace-nowrap",
			// Default state with muted text
			"text-base-content/50",
			// Remove default button styles
			"border-0 bg-transparent outline-none select-none",
			// Hover state - brighten text
			"hover:text-base-content/80",
			// Active state - bright text with high contrast
			"data-active:text-primary-content data-active:font-semibold",
			// Focus visible ring
			"focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-base-100 focus-visible:rounded-lg",
			// Smooth transition
			"transition-all duration-200 ease-out",
			// Cursor
			"cursor-pointer",
			className,
		)}
		{...props}
	/>
));
TabsTab.displayName = "TabsTab";

export const TabsIndicator = React.forwardRef<
	HTMLSpanElement,
	React.ComponentPropsWithoutRef<typeof BaseTabs.Indicator>
>(({ className, ...props }, ref) => (
	<BaseTabs.Indicator
		ref={ref}
		className={cn(
			// Positioning
			"absolute top-1 bottom-1 left-0 -z-10",
			// Size from CSS variables
			"w-(--active-tab-width) translate-x-(--active-tab-left)",
			// Premium styling with gradient and glow
			"rounded-lg",
			"bg-linear-to-b from-primary to-primary/90",
			// Subtle inner shadow for depth
			"shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1),inset_0_1px_0_0_rgba(255,255,255,0.1)]",
			// Ring for extra definition
			"ring-1 ring-primary/20",
			// Smooth spring-like animation
			"transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
			className,
		)}
		{...props}
	/>
));
TabsIndicator.displayName = "TabsIndicator";

export const TabsPanel = React.forwardRef<
	HTMLDivElement,
	React.ComponentPropsWithoutRef<typeof BaseTabs.Panel>
>(({ className, ...props }, ref) => (
	<BaseTabs.Panel
		ref={ref}
		className={cn(
			"mt-4 outline-none",
			// Fade in animation for content
			"animate-in fade-in-0 duration-200",
			// Focus ring
			"focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:rounded-lg",
			className,
		)}
		{...props}
	/>
));
TabsPanel.displayName = "TabsPanel";
